/**
 * Means-of-death identifiers mirrored from the Quake II rerelease `mod_id_t`
 * enumeration in `g_local.h`. The numeric ordering is significant for
 * obituary strings and damage filtering, so we retain the exact sequence from
 * the C++ sources.
 */
export enum DamageMod {
  UNKNOWN = 0,
  BLASTER = 1,
  SHOTGUN = 2,
  SSHOTGUN = 3,
  MACHINEGUN = 4,
  CHAINGUN = 5,
  GRENADE = 6,
  G_SPLASH = 7,
  ROCKET = 8,
  R_SPLASH = 9,
  HYPERBLASTER = 10,
  RAILGUN = 11,
  BFG_LASER = 12,
  BFG_BLAST = 13,
  BFG_EFFECT = 14,
  HANDGRENADE = 15,
  HG_SPLASH = 16,
  WATER = 17,
  SLIME = 18,
  LAVA = 19,
  CRUSH = 20,
  TELEFRAG = 21,
  TELEFRAG_SPAWN = 22,
  FALLING = 23,
  SUICIDE = 24,
  HELD_GRENADE = 25,
  EXPLOSIVE = 26,
  BARREL = 27,
  BOMB = 28,
  EXIT = 29,
  SPLASH = 30,
  TARGET_LASER = 31,
  TRIGGER_HURT = 32,
  HIT = 33,
  TARGET_BLASTER = 34,
  RIPPER = 35,
  PHALANX = 36,
  BRAINTENTACLE = 37,
  BLASTOFF = 38,
  GEKK = 39,
  TRAP = 40,
  CHAINFIST = 41,
  DISINTEGRATOR = 42,
  ETF_RIFLE = 43,
  BLASTER2 = 44,
  HEATBEAM = 45,
  TESLA = 46,
  PROX = 47,
  NUKE = 48,
  VENGEANCE_SPHERE = 49,
  HUNTER_SPHERE = 50,
  DEFENDER_SPHERE = 51,
  TRACKER = 52,
  DBALL_CRUSH = 53,
  DOPPLE_EXPLODE = 54,
  DOPPLE_VENGEANCE = 55,
  DOPPLE_HUNTER = 56,
  GRAPPLE = 57,
  BLUEBLASTER = 58,
  FRIENDLY_FIRE = 59,
}

/**
 * Ordered list of the MOD_* identifiers. Tests use this to verify we stay in
 * lockstep with the rerelease values and to drive lookup helpers that need a
 * stable iteration order.
 */
export const ORDERED_DAMAGE_MODS: readonly DamageMod[] = [
  DamageMod.UNKNOWN,
  DamageMod.BLASTER,
  DamageMod.SHOTGUN,
  DamageMod.SSHOTGUN,
  DamageMod.MACHINEGUN,
  DamageMod.CHAINGUN,
  DamageMod.GRENADE,
  DamageMod.G_SPLASH,
  DamageMod.ROCKET,
  DamageMod.R_SPLASH,
  DamageMod.HYPERBLASTER,
  DamageMod.RAILGUN,
  DamageMod.BFG_LASER,
  DamageMod.BFG_BLAST,
  DamageMod.BFG_EFFECT,
  DamageMod.HANDGRENADE,
  DamageMod.HG_SPLASH,
  DamageMod.WATER,
  DamageMod.SLIME,
  DamageMod.LAVA,
  DamageMod.CRUSH,
  DamageMod.TELEFRAG,
  DamageMod.TELEFRAG_SPAWN,
  DamageMod.FALLING,
  DamageMod.SUICIDE,
  DamageMod.HELD_GRENADE,
  DamageMod.EXPLOSIVE,
  DamageMod.BARREL,
  DamageMod.BOMB,
  DamageMod.EXIT,
  DamageMod.SPLASH,
  DamageMod.TARGET_LASER,
  DamageMod.TRIGGER_HURT,
  DamageMod.HIT,
  DamageMod.TARGET_BLASTER,
  DamageMod.RIPPER,
  DamageMod.PHALANX,
  DamageMod.BRAINTENTACLE,
  DamageMod.BLASTOFF,
  DamageMod.GEKK,
  DamageMod.TRAP,
  DamageMod.CHAINFIST,
  DamageMod.DISINTEGRATOR,
  DamageMod.ETF_RIFLE,
  DamageMod.BLASTER2,
  DamageMod.HEATBEAM,
  DamageMod.TESLA,
  DamageMod.PROX,
  DamageMod.NUKE,
  DamageMod.VENGEANCE_SPHERE,
  DamageMod.HUNTER_SPHERE,
  DamageMod.DEFENDER_SPHERE,
  DamageMod.TRACKER,
  DamageMod.DBALL_CRUSH,
  DamageMod.DOPPLE_EXPLODE,
  DamageMod.DOPPLE_VENGEANCE,
  DamageMod.DOPPLE_HUNTER,
  DamageMod.GRAPPLE,
  DamageMod.BLUEBLASTER,
  DamageMod.FRIENDLY_FIRE,
];

/**
 * Returns the stable string name (e.g. `MOD_RAILGUN`) used by the rerelease
 * for obituary strings and logging.
 */
export function damageModName(mod: DamageMod): string {
  return `MOD_${DamageMod[mod]}`;
}
